/**
 * Packsize Details API
 *
 * Fetches packsize details (b2cAppPoints, dosageFormName, size) for products from Pimcore PIM.
 * Used to display loyalty points and product details on the My Orders page.
 */

export interface PacksizeDetails {
  points: number | null;
  dosageFormName: string;
  size: string;
}

export interface PacksizeDetailsResponse {
  details: Record<string, PacksizeDetails | null>;
}

/**
 * Fetch packsize details for multiple oNumbers in a single batch request
 *
 * @param oNumbers - Array of product oNumbers (e.g., ["0611-30", "0612-60"])
 * @param locale - Optional locale (defaults to current document locale or 'de_DE')
 * @returns Map of oNumber -> details (points, dosageFormName, size) or null if not found
 * @throws Error if the API request fails
 */
export async function fetchPacksizeDetails(
  oNumbers: string[],
  locale?: string
): Promise<PacksizeDetailsResponse> {
  if (oNumbers.length === 0) {
    return { details: {} };
  }

  // Deduplicate oNumbers
  const uniqueONumbers = [...new Set(oNumbers)];

  // Get locale from document or default to de_DE
  const effectiveLocale = locale || document.documentElement.lang?.replace('-', '_') || 'de_DE';

  const response = await fetch('/api/packsize-points', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      oNumbers: uniqueONumbers,
      locale: effectiveLocale,
    }),
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  const data: PacksizeDetailsResponse = await response.json();
  return data;
}
